import type { FunctionComponent, PropsWithChildren } from 'react';
import { useMemo, useState } from 'react';
import { useClient, useEnabledDomiciles } from '../../api';
import type { SupportedLanguage } from '../../api/generated/client';
import { Domicile } from '../../enums';
import { usePresentationMode } from '../../hooks/usePresentationMode';
import type { IWealthNavigatorMicroFrontendProps } from '../../interfaces/wealth-navigator';
import { getDefaultDomicileIsoAlpha2 } from '../../utils/utils';
import { ywuAppContext } from './ywu-app-context';

interface IYwuAppProviderProps extends PropsWithChildren, IWealthNavigatorMicroFrontendProps {}

/**
 * Provider that delivers various important data and helpers for different usages and aspects of the 360 Wealth-Planner application.
 * - Wealth Navigator Micro Frontend related information and event dispatchers
 * - Presentation mode
 * - Application's selected language, selected domicile and thrown errors
 */
export const YwuAppProvider: FunctionComponent<IYwuAppProviderProps> = ({
    isWealthNavigatorMFE,
    dispatchPresentationModeEvent,
    dispatchReturnToSessionsEvent,
    dispatchNavigateToClientContextEvent,
    children,
}) => {
    const { data: activeClient } = useClient();
    const { data: enabledDomiciles } = useEnabledDomiciles();
    const { isPresentationMode, setPresentationMode, presentationModeExitDialog } = usePresentationMode({
        isWealthNavigatorMFE,
        dispatchPresentationModeEvent,
    });

    const [selectedDomicile, setActiveDomicile] = useState<string | undefined>(undefined);
    const [activeLanguage, setActiveLanguage] = useState<SupportedLanguage>('EN');

    const activeDomicile = useMemo(() => {
        if (activeClient) {
            return enabledDomiciles?.find((domicile) => domicile.isoAlpha2 === activeClient?.mainDomicile?.countryCode)?.isoAlpha2 ?? Domicile.GLOBAL;
        } else {
            return selectedDomicile ?? getDefaultDomicileIsoAlpha2(enabledDomiciles ?? []);
        }
    }, [activeClient, enabledDomiciles, selectedDomicile]);

    return (
        <ywuAppContext.Provider
            value={{
                isWealthNavigatorMFE,
                dispatchPresentationModeEvent,
                dispatchReturnToSessionsEvent,
                dispatchNavigateToClientContextEvent,
                isPresentationMode,
                setPresentationMode,
                activeDomicile,
                setActiveDomicile,
                activeLanguage,
                setActiveLanguage,
            }}
        >
            {children}
            {presentationModeExitDialog}
        </ywuAppContext.Provider>
    );
};
